import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Drawing, Question, Profile, Tag, LearningSession } from '../types/database'
import { Sidebar } from './Sidebar'
import { DrawingCanvas } from './DrawingCanvas'
import { RealtimeMessagesCanvas } from './RealtimeMessagesCanvas'
import { SnapshotGallery } from './SnapshotGallery'
import { TimelineGraph } from './TimelineGraph'
import {
  calculateCanvasBounds,
  generateSVGContent,
  extractDrawingPages,
} from '../utils/drawingUtils'
import { getFileUrl } from '../utils/fileUtils'
import html2canvas from 'html2canvas'

type DrawingWithDetails = Drawing & {
  question?: Question | null
  user?: Profile | null
  session?: LearningSession | null
}

interface DrawingDetailProps {
  drawingId: number
  onBack: () => void
}

export function DrawingDetail({ drawingId, onBack }: DrawingDetailProps) {
  const [drawing, setDrawing] = useState<DrawingWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [tagsLoading, setTagsLoading] = useState(false)
  const [isGrayscaleMode, setIsGrayscaleMode] = useState(false)

  useEffect(() => {
    const fetchDrawing = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('drawings')
          .select(`
          *,
          question:questions(
            *,
            question_groups(
              name,
              book_id,
              books(
                name,
                code
              )
            )
          ),
          user:profiles(*),
          session:learning_sessions(*)
        `)
          .eq('id', drawingId)
          .single()
        if (error) throw error
        setDrawing(data as DrawingWithDetails)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch drawing')
      } finally {
        setLoading(false)
      }
    }
    fetchDrawing()
  }, [drawingId])

  useEffect(() => {
    const fetchPdf = async () => {
      if (!drawing?.question?.file) return

      try {
        // @ts-ignore - Supabase types for nested relations are tricky
        const bookCode = drawing.question.question_groups?.books?.code
        const url = await getFileUrl(drawing.question.file, bookCode)
        setPdfUrl(url)
      } catch (err) {
        console.error('Failed to fetch PDF URL:', err)
      }
    }

    if (drawing) {
      fetchPdf()
    }
  }, [drawing])

  useEffect(() => {
    const fetchTags = async () => {
      try {
        // Fetch all available tags
        const { data: tagsData, error: tagsError } = await supabase
          .from('tags')
          .select('*')
          .order('tag_type', { ascending: true })
          .order('name', { ascending: true })

        if (tagsError) throw tagsError
        setAvailableTags(tagsData || [])

        // Fetch current drawing tags
        const { data: drawingTagsData, error: drawingTagsError } = await supabase
          .from('drawing_tags')
          .select('tag_id, tags(*)')
          .eq('drawing_id', drawingId)

        if (drawingTagsError) throw drawingTagsError

        const currentTags = (drawingTagsData || [])
          .map((dt: any) => dt.tags)
          .filter(Boolean) as Tag[]
        setSelectedTags(currentTags)
      } catch (err) {
        console.error('Failed to fetch tags:', err)
      }
    }

    fetchTags()
  }, [drawingId])

  const handleToggleTag = async (tag: Tag) => {
    setTagsLoading(true)
    try {
      const isSelected = selectedTags.some(t => t.id === tag.id)

      if (isSelected) {
        // Remove tag
        const { error } = await supabase
          .from('drawing_tags')
          .delete()
          .eq('drawing_id', drawingId)
          .eq('tag_id', tag.id)

        if (error) throw error
        setSelectedTags(selectedTags.filter(t => t.id !== tag.id))
      } else {
        // Add tag
        const { error } = await supabase
          .from('drawing_tags')
          .insert({ drawing_id: drawingId, tag_id: tag.id })

        if (error) throw error
        setSelectedTags([...selectedTags, tag])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tags')
    } finally {
      setTagsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      const { error } = await supabase
        .from('drawings')
        .delete()
        .eq('id', drawingId)

      if (error) throw error
      onBack()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete drawing')
    }
  }

  const handleDownloadSVG = (animated = false) => {
    if (!drawing || !drawing.drawing_data) return

    const pages = extractDrawingPages(drawing.drawing_data)
    if (pages.length === 0) return

    // Calculate canvas bounds using unified utility
    const bounds = calculateCanvasBounds(pages)

    // Generate SVG content using unified utility
    const svgContent = generateSVGContent(pages, bounds, animated)

    // Create blob and download
    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const suffix = animated ? '-animated' : ''
    link.download = `drawing-${drawingId}-${drawing.question?.name || 'unnamed'}${suffix}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }



  const handleDownloadPNG = async () => {
    // Capture the first page (usually what users want for problems)
    // If we need multi-page support later, we can loop through drawing-canvas-{i}
    const element = document.getElementById('drawing-canvas-0')
    if (!element) {
      alert('캡처할 화면을 찾을 수 없습니다.')
      return
    }

    try {
      const canvas = await html2canvas(element, {
        useCORS: true, // Needed for loading cross-origin images (if any)
        allowTaint: true,

        scale: 2, // High resolution
        logging: false
      } as any)

      const link = document.createElement('a')
      const name = drawing?.question?.name || 'unnamed'
      link.download = `drawing-${drawingId}-${name}.png`
      link.href = canvas.toDataURL('image/png')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('PNG download failed:', err)
      alert('이미지 생성 중 오류가 발생했습니다.')
    }
  }

  const handleDownloadGrayscalePNG = async () => {
    // 1. Switch to grayscale mode
    const previousMode = isGrayscaleMode
    setIsGrayscaleMode(true)

    // 2. Wait for render (short delay)
    await new Promise(resolve => setTimeout(resolve, 300))

    // 3. Perform download using existing logic (slightly modified to accept filename suffix)
    const element = document.getElementById('drawing-canvas-0')
    if (!element) {
      alert('캡처할 화면을 찾을 수 없습니다.')
      setIsGrayscaleMode(previousMode)
      return
    }

    try {
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false
      } as any)

      const link = document.createElement('a')
      const name = drawing?.question?.name || 'unnamed'
      link.download = `drawing-${drawingId}-${name}-grayscale.png`
      link.href = canvas.toDataURL('image/png')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('Grayscale PNG download failed:', err)
      alert('이미지 생성 중 오류가 발생했습니다.')
    } finally {
      // 4. Revert mode
      setIsGrayscaleMode(previousMode)
    }
  }

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  if (error || !drawing) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
          <div className="w-full max-w-7xl mx-auto">
            <button
              onClick={onBack}
              className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span>목록으로 돌아가기</span>
            </button>
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm ml-2">{error || '데이터를 찾을 수 없습니다.'}</span>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            <span>목록으로 돌아가기</span>
          </button>

          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
              솔루션 상세
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDownloadSVG(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <span className="material-symbols-outlined text-base">download</span>
                <span>SVG 다운로드</span>
              </button>
              <button
                onClick={handleDownloadPNG}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <span className="material-symbols-outlined text-base">image</span>
                <span>PNG 다운로드</span>
              </button>
              <button
                onClick={handleDownloadGrayscalePNG}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
              >
                <span className="material-symbols-outlined text-base">palette</span>
                <span>Grayscale PNG</span>
              </button>
              <button
                onClick={() => handleDownloadSVG(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <span className="material-symbols-outlined text-base">animation</span>
                <span>애니메이션 SVG</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <span className="material-symbols-outlined text-base">delete</span>
                <span>삭제</span>
              </button>
            </div>
          </header>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">문제</label>
                  <p className="text-base text-gray-900">{drawing.question?.name || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">사용자</label>
                  <div className="space-y-1">
                    <p className="text-base text-gray-900">
                      {drawing.user ? [drawing.user.last_name, drawing.user.first_name].filter(Boolean).join(' ') || '-' : '-'}
                    </p>
                    {drawing.user?.email && (
                      <p className="text-sm text-gray-500">{drawing.user.email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">등록일시</label>
                  <p className="text-base text-gray-900">
                    {new Date(drawing.created_at).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">ID</label>
                  <p className="text-base text-gray-900">{drawing.id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">세션</label>
                  {drawing.session ? (
                    <p className="text-base text-gray-900">{drawing.session.name}</p>
                  ) : (
                    <p className="text-base text-gray-400">-</p>
                  )}
                </div>
              </div>

              {/* Tag Management Section */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-medium text-gray-500 mb-3">태그</label>
                <div className="space-y-3">
                  {/* Selected tags display */}
                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full text-white"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Tag selection buttons */}
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const isSelected = selectedTags.some(t => t.id === tag.id)
                      return (
                        <button
                          key={tag.id}
                          onClick={() => handleToggleTag(tag)}
                          disabled={tagsLoading}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${isSelected
                            ? 'text-white ring-2 ring-offset-2'
                            : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          style={isSelected ? { backgroundColor: tag.color } : undefined}
                        >
                          {isSelected && (
                            <span className="material-symbols-outlined text-sm">check</span>
                          )}
                          <span>{tag.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-medium text-gray-500 mb-4">드로잉</label>
                <div className="space-y-4">
                  {drawing.drawing_data ? (
                    <>
                      <DrawingCanvas
                        drawingData={drawing.drawing_data}
                        pdfUrl={pdfUrl}
                        isGrayscaleMode={isGrayscaleMode}
                        setIsGrayscaleMode={setIsGrayscaleMode}
                      />

                      <div className="pt-4 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-500 mb-4">활동 밀도 및 스냅샷</label>
                        <TimelineGraph drawingData={extractDrawingPages(drawing.drawing_data)} />
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-500 mb-4">스냅샷 (1초 이상 간격 구간)</label>
                        <SnapshotGallery drawingData={extractDrawingPages(drawing.drawing_data)} />
                      </div>

                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900">
                          원본 데이터 보기
                        </summary>
                        <div className="mt-2 bg-gray-50 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(drawing.drawing_data, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </>
                  ) : (
                    <p className="text-gray-500 italic">저장된 드로잉 데이터가 없습니다</p>
                  )}

                  {drawing.status === 200 && (
                    <div className="pt-4 border-t border-gray-200">
                      <label className="block text-sm font-medium text-gray-500 mb-4">
                        실시간 메시지
                      </label>
                      <RealtimeMessagesCanvas drawingId={drawing.id} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
